<template>
  <!-- Plain elements, not v-snackbar. The old version fought the snackbar's
       #actions slot styling (which collapses child gaps and re-skins buttons),
       and every fix had to be a Vuetify utility class or an inline style. A
       bare fixed element costs nothing and does exactly what it's told. -->
  <Transition name="pwa-toast">
    <div v-if="needRefresh" class="pwa-toast" role="status" aria-live="polite">
      <span class="pwa-toast-dot" aria-hidden="true" />
      <div class="pwa-toast-text">
        <b class="pwa-toast-title">New version available</b>
        <span class="pwa-toast-sub">Reload to get the latest LensPoint.</span>
      </div>
      <div class="pwa-toast-actions">
        <button class="pwa-toast-later" @click="needRefresh = false">Later</button>
        <button class="pwa-toast-reload" :disabled="reloading" @click="reload">
          {{ reloading ? 'Reloading…' : 'Reload' }}
        </button>
      </div>
    </div>
  </Transition>

  <!-- First install: confirm the app is cached and ready offline. Same shell,
       auto-dismissing, single action. -->
  <Transition name="pwa-toast">
    <div v-if="offlineReady" class="pwa-toast" role="status" aria-live="polite">
      <span class="pwa-toast-dot" aria-hidden="true" />
      <div class="pwa-toast-text">
        <b class="pwa-toast-title">Ready to work offline</b>
        <span class="pwa-toast-sub">LensPoint is cached on this device.</span>
      </div>
      <div class="pwa-toast-actions">
        <button class="pwa-toast-later" @click="offlineReady = false">Got it</button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRegisterSW } from 'virtual:pwa-register/vue'

// useRegisterSW registers the service worker (immediate: true) and exposes
// reactive flags. With registerType: 'prompt' the waiting worker stays parked
// until updateServiceWorker(true) activates it and reloads the page.
const { needRefresh, offlineReady, updateServiceWorker } = useRegisterSW({
  immediate: true,
  onRegisterError(err) {
    console.error('[PWA] service worker registration failed:', err)
  },
})

// The offline-ready notice is informational, so it clears itself. The update
// prompt does not — it waits for a decision.
watch(offlineReady, (ready) => {
  if (ready) setTimeout(() => { offlineReady.value = false }, 5000)
})

const reloading = ref(false)

async function reload() {
  reloading.value = true
  try {
    await updateServiceWorker(true)
  } catch (err) {
    console.error('[PWA] update failed:', err)
    reloading.value = false
  }
  // On success the page reloads, so we never reset reloading here.
}
</script>

<style scoped>
.pwa-toast {
  position: fixed;
  z-index: 2200;
  left: 50%;
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  width: min(560px, calc(100vw - 32px));
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 12px 12px 18px;
  border-radius: 999px;
  background: var(--surface);
  box-shadow: var(--shadow-card-hover);
  font-family: var(--font-sans);
}

/* Green dot rather than an icon: it reads as a status light at any size, and
   doesn't need a box of its own to sit in. */
.pwa-toast-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--pos);
  flex-shrink: 0;
}

.pwa-toast-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
}
.pwa-toast-title {
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.pwa-toast-sub {
  font-size: 12.5px;
  color: var(--ink-3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pwa-toast-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.pwa-toast-later {
  padding: 8px 12px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--ink-3);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.pwa-toast-later:hover { background: var(--surface-2); color: var(--ink); }
/* Inverted fill — dark pill on the light theme, light pill on the dark one,
   straight off the ink/bg tokens, so it never needs a per-theme override. */
.pwa-toast-reload {
  padding: 8px 18px;
  border: 0;
  border-radius: 999px;
  background: var(--ink);
  color: var(--bg);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.pwa-toast-reload:hover { background: var(--ink-2); }
.pwa-toast-reload:disabled { opacity: 0.6; cursor: default; }
.pwa-toast-later:focus-visible,
.pwa-toast-reload:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-offset);
}

.pwa-toast-enter-active,
.pwa-toast-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.pwa-toast-enter-from,
.pwa-toast-leave-to { opacity: 0; transform: translate(-50%, 8px); }

/* Phones: the pill can't hold one row of text plus two buttons at this width
   without truncating the message to nothing, so the actions drop to their own
   right-aligned row and the shape relaxes from a pill to a rounded card. */
@media (max-width: 560px) {
  .pwa-toast {
    left: 12px;
    right: 12px;
    bottom: calc(12px + env(safe-area-inset-bottom, 0px));
    width: auto;
    transform: none;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 16px;
  }
  .pwa-toast-dot { margin-top: 5px; }
  .pwa-toast-text { flex: 1 1 auto; }
  .pwa-toast-sub { white-space: normal; }
  .pwa-toast-actions {
    width: 100%;
    justify-content: flex-end;
  }
  /* 44px targets once these are the primary touch surface. */
  .pwa-toast-later,
  .pwa-toast-reload {
    min-height: 44px;
    padding: 10px 20px;
    font-size: 14px;
  }
  .pwa-toast-enter-from,
  .pwa-toast-leave-to { transform: translateY(8px); }
}
</style>
